import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { Axios } from '@utils/api/axios-instance';
import { URL_EXTENSION } from '@utils/constants/app-constants';
import { BaseError } from '@utils/types/shared/base';
import {
  BulkActionRequest,
  BulkActionResponse,
  SessionActionResponse
} from '@utils/types/back-office/operations';
import { OPERATIONS_LIVE_QUERY_KEY } from './use-get-operations-live';

/**
 * Hook to requeue a session
 *
 * Endpoint: POST /api/admin/operations/sessions/{session_id}/requeue
 */
export const useRequeueSession = () => {
  const queryClient = useQueryClient();

  return useMutation<SessionActionResponse, AxiosError<BaseError>, string>(
    async (sessionId: string) => {
      const response = await Axios.post<SessionActionResponse>(
        `${URL_EXTENSION}/admin/operations/sessions/${sessionId}/requeue`
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([OPERATIONS_LIVE_QUERY_KEY]);
      }
    }
  );
};

/**
 * Hook to restart a session from the beginning
 *
 * Endpoint: POST /api/admin/operations/sessions/{session_id}/restart
 */
export const useRestartSession = () => {
  const queryClient = useQueryClient();

  return useMutation<SessionActionResponse, AxiosError<BaseError>, string>(
    async (sessionId: string) => {
      const response = await Axios.post<SessionActionResponse>(
        `${URL_EXTENSION}/admin/operations/sessions/${sessionId}/restart`
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([OPERATIONS_LIVE_QUERY_KEY]);
      }
    }
  );
};

/**
 * Hook to reset a session's status to STATUS_NEW.
 * Destructive — clears error_status and any in-flight queue entry.
 *
 * Endpoint: POST /api/admin/operations/sessions/{session_id}/reset-new
 */
export const useResetSessionToNew = () => {
  const queryClient = useQueryClient();

  return useMutation<SessionActionResponse, AxiosError<BaseError>, string>(
    async (sessionId: string) => {
      const response = await Axios.post<SessionActionResponse>(
        `${URL_EXTENSION}/admin/operations/sessions/${sessionId}/reset-new`
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([OPERATIONS_LIVE_QUERY_KEY]);
      }
    }
  );
};

/**
 * Hook to kill/terminate a session
 *
 * Endpoint: POST /api/admin/operations/sessions/{session_id}/kill
 */
export const useKillSession = () => {
  const queryClient = useQueryClient();

  return useMutation<SessionActionResponse, AxiosError<BaseError>, string>(
    async (sessionId: string) => {
      const response = await Axios.post<SessionActionResponse>(
        `${URL_EXTENSION}/admin/operations/sessions/${sessionId}/kill`
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([OPERATIONS_LIVE_QUERY_KEY]);
      }
    }
  );
};

/**
 * Hook to cleanup a finished/stranded session
 *
 * Endpoint: POST /api/admin/operations/sessions/{session_id}/cleanup
 */
export const useCleanupSession = () => {
  const queryClient = useQueryClient();

  return useMutation<SessionActionResponse, AxiosError<BaseError>, string>(
    async (sessionId: string) => {
      const response = await Axios.post<SessionActionResponse>(
        `${URL_EXTENSION}/admin/operations/sessions/${sessionId}/cleanup`
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([OPERATIONS_LIVE_QUERY_KEY]);
      }
    }
  );
};

/**
 * Hook to change session priority
 *
 * Endpoint: PATCH /api/admin/operations/sessions/{session_id}/priority
 * Body: { priority: number } (1-1000, lower = higher priority)
 */
export const useChangePriority = () => {
  const queryClient = useQueryClient();

  return useMutation<
    SessionActionResponse,
    AxiosError<BaseError>,
    { sessionId: string; priority: number }
  >(
    async ({ sessionId, priority }) => {
      const response = await Axios.patch<SessionActionResponse>(
        `${URL_EXTENSION}/admin/operations/sessions/${sessionId}/priority`,
        { priority }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([OPERATIONS_LIVE_QUERY_KEY]);
      }
    }
  );
};

/**
 * Hook for bulk session actions
 *
 * Endpoint: POST /api/admin/operations/sessions/bulk
 * Body: { session_ids: string[], action: 'requeue' | 'restart' | 'kill' | 'cleanup' }
 */
export const useBulkSessionAction = () => {
  const queryClient = useQueryClient();

  return useMutation<
    BulkActionResponse,
    AxiosError<BaseError>,
    BulkActionRequest
  >(
    async (request: BulkActionRequest) => {
      const response = await Axios.post<BulkActionResponse>(
        `${URL_EXTENSION}/admin/operations/sessions/bulk`,
        request
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([OPERATIONS_LIVE_QUERY_KEY]);
      }
    }
  );
};
